import Page, { Grid, GridColumn } from '@atlaskit/page';
import Spinner from '@atlaskit/spinner';
import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import ReactResizeDetector from 'react-resize-detector';
import styled, { ThemeProvider } from 'styled-components';

import { JoinSiteExperimentConsumerProps, withJoinSiteExperiment } from '../experiments/join-site';
import { AnalyticsConsumerProps, withAnalytics } from '../hoc/withAnalytics';
import UchiIntlProvider from '../i18n/UchiIntlProvider';
import { stripParams } from '../routing';
import Store from '../store';
import FeatureFlagsStore from '../store/featureFlagsStore';
import ProductStore from '../store/productStore';
import UIStore from '../store/uiStore';
import UserStore from '../store/userStore';

import Blanket from './Blanket';
import Drawer from './Drawer';
import Flags from './Flags';
import HomePage from './HomePage';
import InterimHomePage from './InterimHomePage';
import PageTitle from './PageTitle';
import Sidebar from './Sidebar';
import XflowInit from './xflow/XflowInit';

type InjectedProps = {
  featureFlagsStore: FeatureFlagsStore;
  productStore: ProductStore;
  uiStore: UIStore;
  userStore: UserStore;
} & AnalyticsConsumerProps &
  JoinSiteExperimentConsumerProps;

const SpinnerWrapper = styled.div`
  height: 80%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

@observer
class Application extends React.Component<InjectedProps> {
  private atlOrigin: string = safeGetURLParam('atlOrigin');
  private utmSource: string = safeGetURLParam('utm_source');

  componentDidMount() {
    this.props.userStore.getUser();
    this.props.analytics.startUIViewedEvent();
    this.props.featureFlagsStore.fetchFlags();
    when(
      () => this.props.joinSiteExperiment.hasLoadedCohorting,
      () => {
        this.props.productStore.getProductsAndJoinableSites(this.props.joinSiteExperiment.isVariation);
      },
    );
    when(
      () => !this.props.joinSiteExperiment.isLoading,
      () => {
        this.props.joinSiteExperiment.fireExposureEvent({
          treatment: this.shouldShowInterim() ? 'home-interim' : 'home',
          utmSource: this.utmSource,
        });
      },
    );
  }

  render() {
    const { joinSiteExperiment } = this.props;

    if (joinSiteExperiment.isLoading) {
      return (
        <SpinnerWrapper>
          <Spinner data-test-id="home-feature-flag-spinner" delay={50} size={'xlarge'} />
        </SpinnerWrapper>
      );
    }
    return (
      <ThemeProvider theme={{}}>
        <UchiIntlProvider>
          <ReactResizeDetector handleWidth handleHeight onResize={this.onResize} />
          <PageTitle />
          <div>
            <XflowInit />
            <Blanket />
            <Drawer />
            <Flags />
          </div>
          {joinSiteExperiment.isVariation && this.shouldShowInterim() ? (
            <InterimHomePage atlOrigin={this.atlOrigin} utmSource={this.utmSource} />
          ) : (
            <Page navigation={<Sidebar />}>
              <Grid theme={{ columns: 16 }}>
                <GridColumn key="main-content" medium={16}>
                  <HomePage atlOrigin={this.atlOrigin} utmSource={this.utmSource} />
                </GridColumn>
              </Grid>
            </Page>
          )}
        </UchiIntlProvider>
      </ThemeProvider>
    );
  }

  shouldShowInterim() {
    const {
      productStore: { availableProducts, joinableProducts },
    } = this.props;
    return availableProducts.length === 0 && joinableProducts.length > 0;
  }

  private onResize = (width: number): void => {
    this.props.uiStore.setOnMobileMode(width);
  };
}

function safeGetURLParam(paramName: string, defaultValue: string = 'none'): string {
  return stripParams(paramName)[paramName] || defaultValue;
}

export default inject(({ featureFlagsStore, productStore, userStore, uiStore }: Store) => ({
  featureFlagsStore,
  productStore,
  uiStore,
  userStore,
}))(withAnalytics(withJoinSiteExperiment(Application)));
